import { useState, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useData } from "@/hooks/useData";
import { useCurrentUserCompany } from "@/hooks/useCurrentUserCompany";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Truck, Plus, Trash2, Zap } from "lucide-react";
import { FootprintProduct, FootprintDocument } from "@/types/results";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";

interface TransportData {
  id: string;
  vehicleType: string;
  totalWeight: number | "";
  productWeight: number | "";
  distance: number | "";
}

interface DistributionCarbonFootprintStepProps {
  selectedProduct: FootprintProduct;
  onNext: (transportData: TransportData[]) => void;
  onBack: () => void;
  onClose: () => void;
  initialTransportData?: TransportData[];
  editingDistribution?: {
    id: string;
    transport_data: TransportData[];
  };
  isEditing?: boolean;
}

export const DistributionCarbonFootprintStep = ({
  selectedProduct,
  onNext,
  onBack,
  onClose,
  initialTransportData = [],
  editingDistribution,
  isEditing = false
}: DistributionCarbonFootprintStepProps) => {
  const { t } = useLanguage();
  const { company } = useCurrentUserCompany();
  
  // Form state
  const [transportData, setTransportData] = useState<TransportData[]>(() => {
    if (isEditing && editingDistribution?.transport_data) {
      return editingDistribution.transport_data;
    }
    return initialTransportData.length > 0 ? initialTransportData : [createEmptyTransportData()];
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState(false);

  // Fetch transport distributions for vehicle types
  const { data: transportDistributions, loading: transportLoading } = useData<TransportDistribution>({
    collectionName: "setup_transportDistributions",
  });

  // Fetch footprint documents to get the year
  const { data: allFootprintDocuments, loading: footprintLoading } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  // Filter footprint documents by company
  const footprintDocuments = useMemo(() => {
    return allFootprintDocuments?.filter(fp => fp.company_id === company?.id) || [];
  }, [allFootprintDocuments, company?.id]);

  // Get the footprint year for the selected product
  const productYear = useMemo(() => {
    if (!footprintDocuments || !selectedProduct.footprint_id) {
      return new Date().getFullYear();
    }
    
    const footprint = footprintDocuments.find(fp => fp.id === selectedProduct.footprint_id);
    return footprint?.year || new Date().getFullYear();
  }, [footprintDocuments, selectedProduct.footprint_id]);

  // Get active transport distributions (not archived)
  const activeTransportDistributions = useMemo(() => {
    return transportDistributions?.filter(td => !td.isArchived) || [];
  }, [transportDistributions]);

  // Create empty transport data entry
  function createEmptyTransportData(): TransportData {
    return {
      id: `transport_${Date.now()}_${Math.random()}`,
      vehicleType: "",
      totalWeight: "",
      productWeight: "",
      distance: "",
    };
  }

  // Add new transport data row
  const addTransportData = () => {
    setTransportData(prev => [...prev, createEmptyTransportData()]);
  };

  // Remove transport data row
  const removeTransportData = (id: string) => {
    if (transportData.length > 1) {
      setTransportData(prev => prev.filter(item => item.id !== id));
      // Clear errors for removed item
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${id}_vehicleType`];
        delete newErrors[`${id}_totalWeight`];
        delete newErrors[`${id}_productWeight`];
        delete newErrors[`${id}_distance`];
        return newErrors;
      });
    }
  };

  // Update transport data field
  const updateTransportData = (id: string, field: keyof Omit<TransportData, 'id'>, value: string | number) => {
    setTransportData(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, [field]: value }
          : item
      )
    );
    
    // Clear error for this field when user starts typing
    if (errors[`${id}_${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${id}_${field}`];
        return newErrors;
      });
    }
  };

  // Validate individual field
  const validateField = (_id: string, field: keyof Omit<TransportData, 'id'>, value: string | number): string => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return t("results.distribution.carbon.validation.required");
    }
    
    if (field === 'totalWeight' || field === 'productWeight' || field === 'distance') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return t("results.distribution.carbon.validation.invalidNumber");
      }
      if (numValue < 0) {
        return t("results.distribution.carbon.validation.positiveNumber");
      }
      if (numValue === 0) {
        return t("results.distribution.carbon.validation.zeroNumber");
      }
    }
    
    return "";
  };

  // Validate all transport data
  const validateTransportData = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    transportData.forEach(item => {
      const fields: (keyof Omit<TransportData, 'id'>)[] = ['vehicleType', 'totalWeight', 'productWeight', 'distance'];
      
      fields.forEach(field => {
        const error = validateField(item.id, field, item[field]);
        if (error) {
          newErrors[`${item.id}_${field}`] = error;
          isValid = false;
        }
      });
    });
    
    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleNext = () => {
    setIsValidating(true);
    
    if (validateTransportData()) {
      onNext(transportData);
    } else {
      setIsValidating(false);
    }
  };

  // Loading state
  if (transportLoading || footprintLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        <span className="ml-2 text-gray-600">{t("common.loading")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-teal-600" />
            {t("results.distribution.carbon.title")}
          </CardTitle>
          <p className="text-sm text-gray-600">
            {t("results.distribution.carbon.description")}
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-4 bg-teal-50 rounded-lg border border-teal-200">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <Truck className="h-5 w-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">
                {selectedProduct.name}
              </h3>
              <div className="text-sm text-gray-600">
                {selectedProduct.product_type} • {selectedProduct.stone_type_name} • {productYear}
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs bg-blue-50 px-2 py-1 rounded">
              <Zap className="h-3 w-3 text-blue-600" />
              <span className="text-blue-700 font-medium">CO₂</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transport Data Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t("results.distribution.carbon.transportData.title")}</span>
            <Button
              type="button"
              onClick={addTransportData}
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("results.distribution.carbon.transportData.addRow")}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {transportData.map((item, index) => (
            <div key={item.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">
                  {t("results.distribution.carbon.transportData.rowTitle", { number: index + 1 })}
                </h4>
                {transportData.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeTransportData(item.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="space-y-6">
                {/* Vehicle Type */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {t("results.distribution.carbon.transportData.vehicleType")} *
                  </Label>
                  <Select
                    value={item.vehicleType}
                    onValueChange={(value) => updateTransportData(item.id, 'vehicleType', value)}
                  >
                    <SelectTrigger className={`h-11 ${errors[`${item.id}_vehicleType`] ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-teal-500"}`}>
                      <SelectValue placeholder={t("results.distribution.carbon.transportData.selectVehicle")} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTransportDistributions.map((transport) => (
                        <SelectItem key={transport.id} value={transport.vehicleType}>
                          {transport.vehicleType}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Weight and Distance Fields */}
                <div className="flex flex-col md:flex-row gap-4 items-end">
                  {/* Total Weight */}
                  <div className="flex-1 space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      {t("results.distribution.carbon.transportData.totalWeight")} *
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={item.totalWeight}
                        onChange={(e) => updateTransportData(item.id, 'totalWeight', e.target.value)}
                        className={`h-11 pr-8 ${errors[`${item.id}_totalWeight`] ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-teal-500"}`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                        t
                      </span>
                    </div>
                  </div>

                  {/* Product Weight */}
                  <div className="flex-1 space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      {t("results.distribution.carbon.transportData.productWeight")} *
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={item.productWeight}
                        onChange={(e) => updateTransportData(item.id, 'productWeight', e.target.value)}
                        className={`h-11 pr-8 ${errors[`${item.id}_productWeight`] ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-teal-500"}`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                        t
                      </span>
                    </div>
                  </div>

                  {/* Distance */}
                  <div className="flex-1 space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      {t("results.distribution.carbon.transportData.distance")} *
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="0.0"
                        value={item.distance}
                        onChange={(e) => updateTransportData(item.id, 'distance', e.target.value)}
                        className={`h-11 pr-8 ${errors[`${item.id}_distance`] ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-teal-500"}`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                        km
                      </span>
                    </div>
                  </div>
                </div>

                {/* Error Messages Section */}
                {(errors[`${item.id}_vehicleType`] || errors[`${item.id}_totalWeight`] || errors[`${item.id}_productWeight`] || errors[`${item.id}_distance`]) && (
                  <div className="mt-2 space-y-1">
                    {errors[`${item.id}_vehicleType`] && (
                      <div className="flex items-center gap-2 text-red-500 text-sm">
                        <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <span>{t("results.distribution.carbon.transportData.vehicleType")}: {errors[`${item.id}_vehicleType`]}</span>
                      </div>
                    )}
                    {errors[`${item.id}_totalWeight`] && (
                      <div className="flex items-center gap-2 text-red-500 text-sm">
                        <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <span>{t("results.distribution.carbon.transportData.totalWeight")}: {errors[`${item.id}_totalWeight`]}</span>
                      </div>
                    )}
                    {errors[`${item.id}_productWeight`] && (
                      <div className="flex items-center gap-2 text-red-500 text-sm">
                        <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <span>{t("results.distribution.carbon.transportData.productWeight")}: {errors[`${item.id}_productWeight`]}</span>
                      </div>
                    )}
                    {errors[`${item.id}_distance`] && (
                      <div className="flex items-center gap-2 text-red-500 text-sm">
                        <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <span>{t("results.distribution.carbon.transportData.distance")}: {errors[`${item.id}_distance`]}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {activeTransportDistributions.length === 0 && (
            <Alert>
              <AlertDescription>
                {t("results.distribution.carbon.transportData.noVehicleTypes")}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className={`flex gap-2 pt-4 border-t ${isEditing ? 'justify-end' : 'justify-between'}`}>
        {!isEditing && (
          <Button
            variant="outline"
            onClick={onBack}
          >
            {t("common.back")}
          </Button>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleNext}
            disabled={isValidating}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isValidating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              <>
                {isEditing ? t("results.distribution.carbon.update") : t("results.distribution.carbon.calculate")}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
